import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { Package, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { subWeeks, startOfWeek, subDays, format } from 'date-fns';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';
import { realtimeService } from '../../services/realtimeService';
import { orderService } from '../../services/orderService';
import Card from './Card';
import { Skeleton, SkeletonText } from './Skeleton';

// Shared order overview for the Admin and Staff dashboards: one data hook plus
// the stat cards, recent orders list and orders chart. Callers supply the links.

// Order statuses as stored: 'Processing' on submit, then Progress ERP codes.
const STATUS_KEYS = ['Processing', '2', 'h', 'c', 'x'];
const statusLabel = (s) => (s === 'Processing' ? 'Processing' : orderService.getStatusDisplayName(s));
// Chart colours follow the status pill palette used across the portal.
const STATUS_CHART_COLOR = {
  Processing: '#f97316', // orange — awaiting approval / processing
  '2': '#3b82f6',        // blue — in progress
  h: '#d97706',          // amber — on hold
  c: '#16a34a',          // green — complete
  x: '#dc2626',          // red — cancelled
};
export const TIMELINE_DAYS = 30;

const countOrders = (build) =>
  build(supabase.from('orders').select('id', { count: 'exact', head: true })).then((r) => {
    if (r.error) throw r.error;
    return r.count || 0;
  });

// Fetches every row in pages; the Supabase API caps a single response (1000 rows by default).
// Advances by rows actually returned, so a lower server cap can't end the loop early.
const fetchAllCreatedAt = async (sinceIso) => {
  const PAGE = 1000;
  const rows = [];
  for (;;) {
    const { data, error } = await supabase
      .from('orders')
      .select('created_at')
      .gte('created_at', sinceIso)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })
      .range(rows.length, rows.length + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) return rows;
    rows.push(...data);
  }
};

const EMPTY_STATS = { pendingApprovals: 0, rfqsToQuote: 0, onHold: 0, thisWeekOrders: 0, lastWeekOrders: 0 };

/**
 * Loads dashboard order metrics and keeps them live via realtime order events.
 * All counts are head-only count queries: selecting rows and counting them in
 * the browser silently caps at Supabase's 1000-row default.
 */
export function useOrderOverview(channelName) {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [recentOrders, setRecentOrders] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      const now = new Date();
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const timelineStart = subDays(now, TIMELINE_DAYS - 1);
      timelineStart.setHours(0, 0, 0, 0);

      const [
        pendingApprovals,
        onHold,
        thisWeekOrders,
        lastWeekOrders,
        rfqResult,
        recentResult,
        timelineRows,
        ...statusTotals
      ] = await Promise.all([
        countOrders((q) => q.eq('internal_status', 'pending_approval')),
        countOrders((q) => q.in('status', ['h', 'H'])),
        countOrders((q) => q.gte('created_at', thisWeekStart.toISOString())),
        countOrders((q) =>
          q.gte('created_at', lastWeekStart.toISOString()).lt('created_at', thisWeekStart.toISOString())
        ),
        supabase
          .from('inquiry_lists')
          .select('id', { count: 'exact', head: true })
          .in('status', ['submitted', 'under_review']),
        supabase
          .from('orders')
          .select(`
            id,
            order_number,
            status,
            internal_status,
            created_at,
            accounts:user_id ( full_name, company_name )
          `)
          .order('created_at', { ascending: false })
          .limit(6),
        fetchAllCreatedAt(timelineStart.toISOString()),
        ...STATUS_KEYS.map((s) =>
          countOrders((q) => (s.length === 1 ? q.in('status', [s, s.toUpperCase()]) : q.eq('status', s)))
        ),
      ]);

      if (recentResult.error) throw recentResult.error;

      setStats({
        pendingApprovals,
        rfqsToQuote: rfqResult.count || 0,
        onHold,
        thisWeekOrders,
        lastWeekOrders,
      });
      setRecentOrders(recentResult.data || []);
      setStatusCounts(
        STATUS_KEYS.map((key, i) => ({ key, label: statusLabel(key), count: statusTotals[i] }))
          .filter((s) => s.count > 0)
      );

      // Every day in the window, including days with no orders.
      const perDay = {};
      timelineRows.forEach((o) => {
        const d = format(new Date(o.created_at), 'yyyy-MM-dd');
        perDay[d] = (perDay[d] || 0) + 1;
      });
      setTimeline(
        Array.from({ length: TIMELINE_DAYS }, (_, i) => {
          const d = subDays(now, TIMELINE_DAYS - 1 - i);
          return { label: format(d, 'd MMM'), count: perDay[format(d, 'yyyy-MM-dd')] || 0 };
        })
      );
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError(error, 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const unsubscribe = realtimeService.subscribeToOrders(
      channelName,
      () => refresh({ quiet: true }),
      { events: ['INSERT', 'UPDATE', 'DELETE'], showNotifications: true }
    );
    const statusInterval = setInterval(() => {
      setRealtimeConnected(realtimeService.isConnected());
    }, 1000);
    return () => {
      unsubscribe();
      clearInterval(statusInterval);
    };
  }, [channelName, refresh]);

  return { stats, recentOrders, statusCounts, timeline, loading, realtimeConnected, refresh };
}

/** Clickable stat cards; a card with `needsAction` gets the brand highlight. */
export function StatCardGrid({ cards, loading }) {
  const history = useHistory();
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((stat) => {
        const Icon = stat.icon;
        return (
          <button
            key={stat.label}
            onClick={() => history.push(stat.path)}
            className={`text-left bg-white p-5 rounded-lg shadow-sm border transition-shadow duration-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand group ${
              stat.needsAction ? 'border-brand ring-1 ring-brand' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-600">{stat.label}</p>
              <span
                className={`w-8 h-8 rounded-md flex items-center justify-center ${
                  stat.needsAction ? 'bg-brand text-brand-dark' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-2" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 mt-2 tabular-nums">{stat.value}</p>
            )}
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              {stat.trend !== undefined && !loading && stat.trend !== 0 && (
                stat.trend > 0
                  ? <TrendingUp className="w-3.5 h-3.5 text-green-600" aria-label="Up on last week" />
                  : <TrendingDown className="w-3.5 h-3.5 text-red-600" aria-label="Down on last week" />
              )}
              {stat.sub}
              <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
            </p>
          </button>
        );
      })}
    </div>
  );
}

const sectionTitle = (title, action) => (
  <div className="flex items-center justify-between">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {action}
  </div>
);

const statusPill = (order) =>
  order.internal_status === 'pending_approval'
    ? { cls: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Awaiting Approval' }
    : { cls: orderService.getStatusColor(order.status), label: statusLabel(order.status) || 'Unknown' };

/** Latest orders with status pills. `orderPath(order)` builds each row's link; omit `viewAllPath` to hide "View all". */
export function RecentOrdersCard({ orders, loading, viewAllPath, orderPath }) {
  const history = useHistory();
  return (
    <Card
      padding="sm"
      header={sectionTitle('Recent Orders', viewAllPath &&
        <button
          onClick={() => history.push(viewAllPath)}
          className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          View all <ArrowRight className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
    >
      {loading ? (
        <SkeletonText lines={6} />
      ) : orders.length > 0 ? (
        <div className="space-y-0.5">
          {orders.map((order) => {
            const pill = statusPill(order);
            return (
              <button
                key={order.id}
                onClick={() => history.push(orderPath(order))}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left"
              >
                <Package className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{order.order_number}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {order.accounts?.company_name || order.accounts?.full_name || 'Unknown customer'}
                  </p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${pill.cls}`}>
                  {pill.label}
                </span>
                <span className="w-16 text-right text-xs text-gray-500 flex-shrink-0">
                  {format(new Date(order.created_at), 'd MMM')}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-8">No orders yet</p>
      )}
    </Card>
  );
}

/** Orders by status (ring) or per day over the last TIMELINE_DAYS days (bars). */
export function OrdersChartCard({ statusCounts, timeline, loading }) {
  const [chartType, setChartType] = useState('status');

  const option = chartType === 'status'
    ? {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, textStyle: { color: '#374151' } },
        series: [{
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '45%'],
          label: { show: false },
          data: statusCounts.map((s) => ({
            name: s.label,
            value: s.count,
            itemStyle: { color: STATUS_CHART_COLOR[s.key] },
          })),
        }],
      }
    : {
        tooltip: { trigger: 'axis' },
        grid: { left: 32, right: 12, top: 16, bottom: 28 },
        xAxis: {
          type: 'category',
          data: timeline.map((t) => t.label),
          axisLabel: { color: '#6b7280', interval: 6 },
          axisLine: { lineStyle: { color: '#e5e7eb' } },
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLabel: { color: '#6b7280' },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
        },
        series: [{
          name: 'Orders',
          type: 'bar',
          data: timeline.map((t) => t.count),
          itemStyle: { color: '#1a1818', borderRadius: [3, 3, 0, 0] },
          emphasis: { itemStyle: { color: '#fed402' } },
        }],
      };

  return (
    <Card
      padding="sm"
      header={sectionTitle('Orders',
        <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs" role="tablist">
          {[['status', 'By status'], ['timeline', `Last ${TIMELINE_DAYS} days`]].map(([key, label], i) => (
            <button
              key={key}
              role="tab"
              aria-selected={chartType === key}
              onClick={() => setChartType(key)}
              className={`px-3 py-1.5 font-medium transition-colors ${i > 0 ? 'border-l border-gray-200' : ''} ${
                chartType === key ? 'bg-brand-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    >
      {loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <ReactECharts option={option} notMerge style={{ height: '288px' }} />
      )}
    </Card>
  );
}

/** "Live" / "Connecting…" indicator for realtime updates. */
export function LiveIndicator({ connected }) {
  return (
    <span
      className="flex items-center gap-1.5 px-2 text-xs font-medium text-gray-600"
      title={connected ? 'Order changes appear automatically' : 'Connecting to live updates'}
    >
      <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
      {connected ? 'Live' : 'Connecting…'}
    </span>
  );
}
